import { usePost } from "@albertine/api-client";
import * as Sentry from "@sentry/react";
import { useEffect, useEffectEvent } from "react";
import { useComponentState } from "../../utils/customHook.util";
import { Flex } from "../Common/Flex";
import Label from "../Common/Label";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import ExternalImageSelect from "./ExternalImageSelect";

const minimumWidth = 400;

async function loadImages(
    imageUrls: string[],
    callback: (images: HTMLImageElement[]) => void,
) {
    const images = imageUrls.map(
        (url) =>
            new Promise<{ success: boolean; image: HTMLImageElement }>((resolve) => {
                const img = new Image();
                img.onload = () => {
                    resolve({ success: true, image: img });
                };
                img.onerror = () => resolve({ success: false, image: img });
                img.src = url;
            }),
    );

    const loadedImages = await Promise.all(images);

    callback(
        loadedImages
            .filter((it) => it.success)
            .map((it) => it.image)
            .filter((it) => it.width >= minimumWidth),
    );
}

type ImageImageCrawlerProps = {
    baseUrl: string;
    initialValues: string[];
    onSelect: (urls: string[]) => void;
};

type ImageImageCrawlerState = {
    loading: boolean;
    imageUrls: string[] | null | undefined;
    images: HTMLImageElement[] | undefined;
    selectedImages: string[];
};

function ImageCrawler(props: ImageImageCrawlerProps) {
    const { baseUrl, initialValues, onSelect } = props;
    const { state, setState } = useComponentState<ImageImageCrawlerState>({
        loading: true,
        imageUrls: undefined,
        images: undefined,
        selectedImages: initialValues,
    });
    const { mutate } = usePost("websiteImages", {
        onMutate: () => setState({ loading: true }),
        onSuccess: (data) => {
            loadImages(data.images ?? [], (loadedImages) => {
                setState({ images: loadedImages, loading: false });
            });
        },
        onError: (error: unknown) => {
            setState({ loading: false });
            Sentry.captureException(error, {
                extra: {
                    message: `ImageCrawler: getWebsiteImages with url ${baseUrl} failed`,
                    location:
                        typeof window !== "undefined"
                            ? window.location.href
                            : undefined,
                },
            });
            window.alert(error instanceof Error ? error.message : String(error));
        },
    });

    const fetchImages = useEffectEvent((url: string) => {
        mutate({ json: { baseUrl: url } });
    });

    useEffect(() => {
        fetchImages(baseUrl);
    }, [baseUrl]);

    return (
        <Flex.Row gap="medium">
            {state.loading && (
                <Flex.Column alignItems="center" justifyContent="center">
                    <LoadingSpinner />
                </Flex.Column>
            )}
            {state.images?.length === 0 && (
                <Flex.Column gap="small" alignItems="center" justifyContent="center">
                    <Label.Medium.Light.N10>
                        Unable to find suitable images from
                    </Label.Medium.Light.N10>
                    <a href={baseUrl} target="_blank" rel="noreferrer">
                        <Label.Medium.SemiBold.N10>{baseUrl}</Label.Medium.SemiBold.N10>
                    </a>
                </Flex.Column>
            )}
            <ExternalImageSelect
                allUrls={(state.images || []).map((image) => image.currentSrc)}
                initialUrlSelections={initialValues}
                onSelect={(selected) => {
                    onSelect(selected);
                }}
            />
        </Flex.Row>
    );
}

export default ImageCrawler;
